import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Book } from '../../types/Book';
import { bookService } from '../../services/bookService';
import { User } from '../../services/authService';

interface BookCardProps {
  book: Book;
  user: User | null;
  onEdit: (book: Book) => void;
  onDelete: (id: string) => void;
  onReadOnline?: (book: Book) => void;
  onFavoriteToggle?: (book: Book, isFavorite: boolean) => void;
  onStatusChange?: (book: Book, newStatus: string) => void;
}

const BookCard: React.FC<BookCardProps> = ({ book, user, onEdit, onDelete, onReadOnline, onFavoriteToggle, onStatusChange }) => {
  const navigate = useNavigate();
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [isFavorite, setIsFavorite] = useState(book.isFavorite || false);
  const [isToggling, setIsToggling] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
  const [isCreatingNote, setIsCreatingNote] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Generate Gutenberg cover URL using book ID
  const gutenbergCoverUrl = `https://www.gutenberg.org/cache/epub/${book.id}/pg${book.id}.cover.medium.jpg`;
  
  // Use custom cover URL if available, otherwise use Gutenberg cover
  const coverUrl = book.coverUrl || gutenbergCoverUrl;

  // Sync favorite state when book prop changes
  useEffect(() => {
    setIsFavorite(book.isFavorite || false);
  }, [book.isFavorite]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowActionMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleReadOnline = () => {
    setShowActionMenu(false);
    if (onReadOnline) {
      onReadOnline(book);
    } else {
      window.open(`https://www.gutenberg.org/cache/epub/${book.id}/pg${book.id}-images.html`, '_blank');
    }
  };

  const handleEdit = () => {
    setShowActionMenu(false);
    onEdit(book);
  };

  const handleDelete = () => {
    setShowActionMenu(false);
    onDelete(book.id);
  };

  const handleFavoriteToggle = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering parent click events
    
    if (isToggling) return; // Prevent multiple rapid clicks
    
    setIsToggling(true);
    try {
      const newFavoriteStatus = !isFavorite;
      
      if (newFavoriteStatus) {
        const success = await bookService.addToFavorites(book.id);
        if (success) {
          setIsFavorite(true);
          if (onFavoriteToggle) {
            onFavoriteToggle(book, true);
          }
        }
      } else {
        const success = await bookService.removeFromFavorites(book.id);
        if (success) {
          setIsFavorite(false);
          if (onFavoriteToggle) {
            onFavoriteToggle(book, false);
          }
        }
      }
    } catch (error) {
      console.error('Error toggling favorite status:', error);
    } finally {
      setIsToggling(false);
    }
  };

  const handleMarkAsReading = async () => {
    setShowActionMenu(false);
    if (isUpdatingStatus) return;
    
    setIsUpdatingStatus(true);
    try {
      const success = await bookService.updateReadingProgress(book.id, {
        status: 'reading'
      });
      
      if (success && onStatusChange) {
        const updatedBook = { ...book, readingStatus: 'reading' as const };
        onStatusChange(updatedBook, 'reading');
      }
    } catch (error) {
      console.error('Error marking book as reading:', error);
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleViewDetails = () => {
    setShowActionMenu(false);
    navigate(`/books/${book.id}`);
  };

  const handleViewNotes = async () => {
    setShowActionMenu(false);
    if (!user || isCreatingNote) return;
    
    setIsCreatingNote(true);
    try {
      // Check if note already exists
      let noteId = await bookService.getBookNoteId(book.id);
      
      // If note doesn't exist, create it
      if (!noteId) {
        noteId = await bookService.createBookNote(book.id, user.id, book.title);
      }
      
      // Navigate to note
      if (noteId) {
        navigate(`/notes/${noteId}`);
      }
    } catch (error) {
      console.error('Error accessing book notes:', error);
    } finally {
      setIsCreatingNote(false);
    }
  };

  return (
    <div className="book-card">
      <div className="book-content">
        {/* Cover on the left */}
        <div className="book-cover-container">
          <img 
            src={coverUrl} 
            alt={`${book.title} cover`}
            className="book-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              // If the primary cover fails and we haven't tried Gutenberg yet, try it
              if (book.coverUrl && target.src === book.coverUrl) {
                target.src = gutenbergCoverUrl;
              } else {
                // If both fail, hide the image
                target.style.display = 'none';
              }
            }}
          />
        </div>

        {/* Book details on the right */}
        <div className="book-details">
          <div className="book-info">
            <div className="book-title-row">
              <h3 className="book-title">{book.title}</h3>
              <button 
                className={`favorite-btn ${isFavorite ? 'is-favorite' : ''} ${isToggling ? 'toggling' : ''}`}
                onClick={handleFavoriteToggle}
                disabled={isToggling}
                title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
              >
                {isToggling ? '⏳' : (isFavorite ? '❤️' : '🤍')}
              </button>
            </div>
            <p className="book-author">by {book.author}</p>
            {book.publicationDate && (
              <p className="book-publication-date">Published: {book.publicationDate}</p>
            )}
            {book.genre && (
              <p className="book-genre">{book.genre}</p>
            )}
            {book.favoriteNotes && (
              <div className="favorite-notes">
                <small>📝 {book.favoriteNotes}</small>
              </div>
            )}
          </div>

          <div className="book-meta">
            {book.pageCount && (
              <p className="book-page-count">{book.pageCount} pages</p>
            )}
            {book.position_slot && (
              <div className="book-position-slot">
                📍 {book.position_slot}
              </div>
            )}
            {book.favoritedAt && (
              <div className="favorited-at">
                <small>❤️ Added {new Date(book.favoritedAt).toLocaleDateString()}</small>
              </div>
            )}
            {/* Reading Progress */}
            {(book.currentPage !== undefined && book.currentPage > 0) && (
              <div className="book-reading-progress">
                {book.totalPages ? (
                  <>
                    <div className="progress-info">
                      <span className="progress-text">Page {book.currentPage} / {book.totalPages}</span>
                      {book.readingProgress !== undefined && (
                        <span className="progress-percentage">({Math.round(book.readingProgress)}%)</span>
                      )}
                    </div>
                    <div className="progress-bar-container">
                      <div 
                        className="progress-bar-fill" 
                        style={{ 
                          width: `${Math.min((book.currentPage / book.totalPages) * 100, 100)}%` 
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <div className="progress-info">
                    <span className="progress-text">Page {book.currentPage}</span>
                    {book.readingProgress !== undefined && (
                      <span className="progress-percentage">({Math.round(book.readingProgress)}%)</span>
                    )}
                  </div>
                )}
                {book.readingStatus && book.readingStatus !== 'not_started' && (
                  <div className="reading-status-badge">
                    {book.readingStatus === 'reading' && '📖 Reading'}
                    {book.readingStatus === 'completed' && '✅ Completed'}
                    {book.readingStatus === 'paused' && '⏸️ Paused'}
                    {book.readingStatus === 'abandoned' && '❌ Abandoned'}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action menu */}
        <div className="book-actions" ref={menuRef}>
          <button 
            className="action-menu-trigger"
            onClick={() => setShowActionMenu(!showActionMenu)}
            title="More actions"
          >
            ⋮
          </button>
          
          {showActionMenu && (
            <div className="action-menu">
              {book.readingStatus !== 'reading' && (
                <button 
                  className="action-menu-item"
                  onClick={handleMarkAsReading}
                  disabled={isUpdatingStatus}
                >
                  {isUpdatingStatus ? '⏳' : '📖'} Mark as Reading
                </button>
              )}
              <button 
                className="action-menu-item"
                onClick={handleViewDetails}
              >
                📄 View Details
              </button>
              <button 
                className="action-menu-item"
                onClick={handleViewNotes}
                disabled={isCreatingNote || !user}
              >
                {isCreatingNote ? '⏳' : '📝'} View Notes
              </button>
              <button 
                className="action-menu-item"
                onClick={handleReadOnline}
              >
                📖 Read Online
              </button>
              <button 
                className="action-menu-item"
                onClick={handleEdit}
              >
                ✏️ Edit
              </button>
              <button 
                className="action-menu-item action-menu-item-danger"
                onClick={handleDelete}
              >
                🗑️ Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BookCard;